import {
  OPENAI_REGISTRATION_EVENT,
  OPENAI_SEARCH_EVENT,
  type OpenAiConversionEventType,
} from '@dupe/common/constants'
import { pruneDailyMarks, todayStamp } from './dailyConversionId'
import {
  relayOpenAiRegistrationConversion,
  relayOpenAiSearchConversion,
} from './openAiConversionRelay'
import type { DupeSearchType } from './googleAdsConversions'

/**
 * OpenAI (ChatGPT Ads) pixel copy of the two conversions marketing bids on
 * (DUP-2570) — the fourth platform alongside Google, Meta and TikTok, fired
 * from the same call sites so all four see the same definition of "a dupe
 * performed" and "an account created".
 *
 * ⚠️ The browser copy's transport is the OpenAI pixel loaded in
 * `apps/web/src/components/providers/AppProviders.tsx` (`openai-pixel-base` /
 * `openai-pixel-loader`). Removing those scripts kills the pixel copy of both
 * conversions with no error and no test failure — every call here just finds
 * `window.oaiq` absent and no-ops, leaving only the server copy below.
 *
 * ⚠️ There is a SERVER leg too, decided here and only relayed by the api:
 * at the same moment this module sends a conversion to the pixel it posts the
 * same event name and id to the api (`openAiConversionRelay.ts`), which
 * forwards it to OpenAI's Conversions API. OpenAI keeps whichever copy lands
 * first. It covers the browser whose pixel never delivers — an ad blocker, a
 * tab closed before the SDK loaded. The api accepts only the id shapes this
 * module stamps, so a new id shape needs the api's shape check updated too.
 *
 * ⚠️ Both event names are a cross-platform contract, not ours to rename. Each
 * is a STANDARD OpenAI event chosen because custom events cannot be campaign
 * optimization goals, and a campaign binds to exactly one event at creation
 * and cannot be repointed afterwards. Renaming either one silently detaches a
 * live campaign from its conversions.
 */

/**
 * `contents_viewed` is the closest biddable standard event to "ran a search" —
 * OpenAI has no search event. Mapped in the dashboard as `Search Event`.
 */
const SEARCH_EVENT = OPENAI_SEARCH_EVENT

/** Mapped in the dashboard as `Registration Complete`. */
const REGISTRATION_EVENT = OPENAI_REGISTRATION_EVENT

/**
 * The data shape each standard event accepts, and the ONLY fields it accepts —
 * the SDK drops an event outright for one undocumented key, so these lists are
 * a hard contract, not guidance.
 *
 *   contents        `type`, `amount`, `currency`, `contents`
 *   customer_action `type`, `amount`, `currency`
 *
 * Which is why the search-type breakdown rides inside `contents` on a search
 * and has no equivalent at all on a signup.
 */
const CONTENTS_SHAPE = { type: 'contents' } as const
const CUSTOMER_ACTION_SHAPE = { type: 'customer_action' } as const

const firedConversions = new Set<string>()
const STORAGE_PREFIX = 'openai-conversion:'
const LOCK_PREFIX = 'openai-conversion-lock:'
const PENDING_PREFIX = 'openai-conversion-pending:'
const LEASE_PREFIX = 'openai-conversion-lease:'

/**
 * ⚠️ Every prefix above must stay disjoint from `STORAGE_PREFIX` under
 * `startsWith`, which is what the mark sweep tests. The hyphen where a colon
 * would be is the only thing separating them — `openai-conversion-lease:` does
 * not start with `openai-conversion:`. A new prefix that breaks that gets
 * silently eaten by `pruneStaleMarks` and read back as a fired mark.
 */

/**
 * Mirrors `metaConversions.ts` for the same reason: the search conversion is
 * capped per DAY (see `dailyConversionId.ts`), so the mark has to outlive a
 * tab. `sessionStorage` is per-tab and would let a second tab re-fire the same
 * day's conversion; `localStorage` is what makes the cap hold locally rather
 * than leaning entirely on OpenAI's own event-id dedup.
 */
const hasFired = (key: string): boolean => {
  if (firedConversions.has(key)) return true

  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${key}`) !== null
  } catch {
    // localStorage can be unavailable in privacy-restricted browsers. The
    // module-level set still prevents duplicate React effects in that case.
    return false
  }
}

/**
 * How long another tab should treat an in-flight send as "someone is already
 * on this". Long enough to outlast the longest legitimate attempt, short
 * enough that a tab which dies mid-send does not suppress a real retry.
 *
 * Derived, not guessed at: a search waits up to `CONFIRM_DEADLINE_MS` (10s)
 * for the SDK to confirm, and a signup can spend its own 10s waiting for the
 * queue stub to exist BEFORE that clock even starts — so the longest honest
 * attempt is ~20s. The remaining 5s is margin, because both budgets are
 * measured by `setTimeout` and a backgrounded tab has its timers throttled,
 * which stretches 20s of budget past 20s of wall clock.
 *
 * ⚠️ Neither 10s figure is measured — they are this module's own estimates,
 * and nothing in the app records real third-party script load time (Datadog
 * RUM runs with `trackResources: false`). If either deadline moves, this has
 * to move with it.
 */
const LEASE_TTL_MS = 25_000

/**
 * The third state the two-state mark cannot express: sent, but not yet
 * confirmed delivered.
 *
 * A mark means "delivered, never send again today". Deliberately it is not
 * written until the SDK confirms — see `markWhenDelivered`. That leaves a
 * window, from the push onto `oaiq.q` until the SDK loads, in which another
 * tab reads no mark and sends the same conversion again. The Web Lock cannot
 * close it: the lock callback returns as soon as the event is queued, and
 * holding it across the wait would let a frozen background tab block a live
 * one from reporting at all — trading a duplicate for a loss, which is the
 * wrong way round here.
 *
 * So the sending tab leaves a short, self-expiring note instead. Expiry is
 * judged on READ, never by the sweep: the case this exists for is a tab that
 * died, and a dead tab runs no sweep.
 */
const leaseHeld = (key: string): boolean => {
  try {
    const raw = window.localStorage.getItem(`${LEASE_PREFIX}${key}`)
    if (raw === null) return false

    const takenAt = Number.parseInt(raw, 10)
    const live = Number.isFinite(takenAt) && Date.now() - takenAt < LEASE_TTL_MS
    if (!live) window.localStorage.removeItem(`${LEASE_PREFIX}${key}`)
    return live
  } catch {
    // See hasFired. Without storage there is no cross-tab signal at all, and
    // OpenAI's event-id dedup is the only remaining backstop.
    return false
  }
}

const takeLease = (key: string): void => {
  try {
    window.localStorage.setItem(`${LEASE_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // See hasFired.
  }
}

/**
 * Called the moment this tab stops working on the send — delivered, given up,
 * or threw. Waiting for the TTL instead would block this browser's own next
 * attempt for the rest of the window; the TTL is only there for the tab that
 * never got to call this.
 */
const releaseLease = (key: string): void => {
  try {
    window.localStorage.removeItem(`${LEASE_PREFIX}${key}`)
  } catch {
    // See hasFired.
  }
}

/**
 * A conversion that was attempted and never confirmed delivered, so a later
 * page load can try again.
 *
 * Both conversions need this, for the same reason and with different limits.
 * A queued event whose SDK never arrives dies with the page: nothing drains
 * the queue, and the mark was deliberately never written. A signup has no
 * natural second attempt at all — the auth effect reports that user as
 * existing from then on. A search does have one, the user's next search, but
 * only if they run another; close the tab after one dupe and the conversion
 * is simply gone. That is an undercount, and an undercount is exactly what
 * makes a cost-per-dupe uncomparable against the other three platforms.
 *
 * Stored as `writtenAt|attempts|detail` — see `parsePending` for what an
 * older, narrower record does and does not survive.
 */
const PENDING_TTL_MS = 30 * 24 * 60 * 60 * 1000

/**
 * A record that keeps failing is a record that will keep failing — a blocked
 * domain, an extension, a browser that drops the script. Without a cap it is
 * re-sent on every page load for the whole attribution window, and the only
 * thing collapsing those copies is a dedup rule we have read and never
 * watched work.
 */
const MAX_PENDING_ATTEMPTS = 5

type PendingRecord = {
  storageKey: string
  key: string
  writtenAt: number
  attempts: number
  detail: string
}

/**
 * The `writtenAt|attempts|detail` layout, encoded and decoded in exactly one
 * place each. It used to be hand-split at three call sites, and the two
 * readers had already drifted: a corrupt `writtenAt` was refreshed to now by
 * one and aged to zero by the other, so the same damaged record survived or
 * died depending on which path reached it first.
 *
 * Zero is the deliberate reading. A record that renews its own age never ages
 * out, which is the failure `rememberPending` exists to avoid; a record we
 * cannot date is one we let the TTL collect.
 *
 * A bare `writtenAt` from an older build still parses, so the format widened
 * without a migration — but only a SIGNUP record survives that. A search
 * record replays off its `detail` (the search type), and an old record has
 * none, so the flush drops it rather than send a payload it cannot rebuild.
 * Widen the format again and the same applies: give search records a default
 * the flush can act on, or accept losing the ones written by the old build.
 */
const formatPending = (
  writtenAt: number,
  attempts: number,
  detail: string
): string => `${writtenAt}|${attempts}|${detail}`

const parsePending = (
  raw: string
): { writtenAt: number; attempts: number; detail: string } => {
  const [writtenAtRaw = '', attemptsRaw = '', ...rest] = raw.split('|')
  const writtenAt = Number.parseInt(writtenAtRaw, 10)
  const attempts = Number.parseInt(attemptsRaw, 10)

  return {
    writtenAt: Number.isFinite(writtenAt) ? writtenAt : 0,
    attempts: Number.isFinite(attempts) ? attempts : 0,
    detail: rest.join('|'),
  }
}

/**
 * Create-if-absent, never refresh. Re-writing would move `writtenAt`, and a
 * record that renews its own age never ages out.
 */
const rememberPending = (key: string, detail: string): void => {
  try {
    const storageKey = `${PENDING_PREFIX}${key}`
    if (window.localStorage.getItem(storageKey) !== null) return
    window.localStorage.setItem(
      storageKey,
      formatPending(Date.now(), 0, detail)
    )
  } catch {
    // See hasFired. A browser that cannot persist this simply loses the retry.
  }
}

/**
 * Counts REAL sends: not page loads, not flushes that declined to send, and
 * NOT a dispatch that threw.
 *
 * ⚠️ Called AFTER `oaiq` returns, never before. Counting before the call
 * spends the cross-page retry budget on failures that never reached the
 * pixel: a stub that throws persistently — a consent extension, an SDK
 * internal error — re-enters on a 200ms timer for the whole 10s deadline, so
 * one page load alone books ~50 attempts. The next flush then reads
 * `attempts >= MAX_PENDING_ATTEMPTS` and deletes the very record that exists
 * to carry the conversion to a later visit.
 *
 * Increment only. The record is created by `rememberPending` before the
 * attempt, so a throw still leaves something to retry.
 */
const countPendingDispatch = (key: string): void => {
  try {
    const storageKey = `${PENDING_PREFIX}${key}`
    const existing = window.localStorage.getItem(storageKey)
    if (existing === null) return

    const record = parsePending(existing)
    window.localStorage.setItem(
      storageKey,
      formatPending(record.writtenAt, record.attempts + 1, record.detail)
    )
  } catch {
    // See hasFired.
  }
}

const DATED_KEY = /:\d{4}-\d{2}-\d{2}$/

/**
 * Required of every `dailyConversionId` consumer. Drop marks that can no
 * longer be read, so `localStorage` does not gain keys forever as the price
 * of the durability the daily cap needs. The three mark classes (registration
 * kept, dated live for their day, undated aged out) are `pruneDailyMarks`'s,
 * shared with the relay's own marks.
 *
 * Leases are swept here too, and are only hygiene — the keys a dead tab left
 * behind. They are judged expired on read; a dead tab runs no sweep, and this
 * one only runs on a successful delivery.
 */
const pruneStaleMarks = (): void => {
  pruneDailyMarks(STORAGE_PREFIX, `${STORAGE_PREFIX}${REGISTRATION_EVENT}:`)

  try {
    const leaseCutoff = Date.now() - LEASE_TTL_MS
    const stale: string[] = []

    for (let i = 0; i < window.localStorage.length; i += 1) {
      const k = window.localStorage.key(i)
      if (!k?.startsWith(LEASE_PREFIX)) continue
      const takenAt = Number.parseInt(window.localStorage.getItem(k) ?? '', 10)
      if (!Number.isFinite(takenAt) || takenAt < leaseCutoff) stale.push(k)
    }

    for (const k of stale) window.localStorage.removeItem(k)
  } catch {
    // See hasFired.
  }
}

/** Exported for the spec. */
export const pruneStaleOpenAiConversionMarks = pruneStaleMarks

/**
 * Only ever called once `oaiq` was present and did not throw. The mark is
 * durable for the rest of the day, so writing it on a no-op (the pixel stub
 * not attached yet, or the pixel removed) would suppress the conversion until
 * tomorrow with no way to retry.
 */
const markFired = (key: string): void => {
  firedConversions.add(key)
  pruneStaleMarks()

  // Delivered, so there is nothing left for a later page load to retry.
  // `dropPending` brings its own try, and that separation is the point:
  // sharing one with the mark below means a `setItem` that throws — quota, or
  // a privacy-restricted browser — leaves the retry record behind on a
  // conversion that actually landed, and every later page load sends it again
  // until the record ages out.
  dropPending(`${PENDING_PREFIX}${key}`)

  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${key}`, `${Date.now()}`)
  } catch {
    // See hasFired: privacy-restricted browsers throw here.
  }

  // Last, and after the mark. The lease and the mark are the two things
  // another tab reads, and releasing the lease first opens a window where it
  // sees neither and sends the same conversion again. The signup path has no
  // Web Lock to fall back on, so that window is the whole guarantee there.
  //
  // No `finally` around the mark: a browser that threw writing it will throw
  // removing the lease too, and a lease left behind expires on read at its
  // TTL rather than blocking anything indefinitely.
  releaseLease(key)
}

const CONFIRM_POLL_MS = 200
const CONFIRM_DEADLINE_MS = 10_000

/**
 * Whether the real SDK is up, as opposed to the inline queue stub.
 *
 * Verified in the shipped bundle rather than assumed: on evaluation the SDK
 * replaces the stub and sets `loaded = true`, `__oaiqInitialized = true` and
 * `version` in one statement, and drains the stub's `.q`. Read from
 * `https://bzrcdn.openai.com/sdk/oaiq.min.js`, SDK version 0.1.41,
 * etag `0x8DF0A8EEA37FC12`.
 *
 * ⚠️ Both flags are undocumented internals on an unversioned CDN path — the
 * ads docs never mention inspecting `window.oaiq` at all. `__oaiqInitialized`
 * is checked as well as `loaded` because it is the one the SDK uses as its
 * own already-installed guard, so it is the likelier of the two to survive a
 * rewrite. If OpenAI drops both, this reads false forever: no conversion is
 * ever marked, and every one of them retries on the next page load.
 *
 * ⚠️ What this proves is narrower than the name suggests: the real dispatcher
 * is installed and took the call. It is NOT network delivery — the SDK sets
 * these before consent resolution and batches events internally, flushing on
 * page hide. There is no browser-side signal for actual delivery.
 */
const sdkLoaded = (): boolean =>
  window.oaiq?.loaded === true || window.oaiq?.__oaiqInitialized === true

/**
 * Keys dispatched into the queue but not yet confirmed. Stops a second search
 * in the same page from queueing the same event again while we are waiting —
 * if the SDK is blocked it will stay blocked for this page, so re-queueing
 * would only pile up copies for a flush that never comes.
 */
const awaitingConfirmation = new Set<string>()

/**
 * Handing an event to the queue is NOT delivery, and this is the difference
 * that matters on OpenAI. Pushing onto `oaiq.q` cannot throw, so "the call did
 * not throw" says only that the stub exists. If the SDK download never lands —
 * blocked by an extension, a failed request, a blocked domain — the event sits
 * in an array that nothing ever drains.
 *
 * So the mark, which is what enforces the daily cap, waits until the SDK has
 * actually loaded. An event that was queued and never picked up stays unmarked
 * and therefore retryable on the next page load, instead of writing off the
 * rest of the user's day. Meta can afford the simpler rule because a server
 * copy of every Meta event arrives independently. OpenAI's server copy is
 * best-effort (one relay per conversion, in-memory retries at the api), so the
 * browser event is still the one worth protecting.
 *
 * Worst case is a duplicate: a queue that flushes late plus a retry from a
 * later page load. Both carry the same event id, which is what OpenAI's dedup
 * collapses. The lease narrows that window across tabs; it cannot close it
 * across devices or across a cleared storage, and nothing here can.
 */
/**
 * KNOWN LIMIT, accepted rather than fixed: the local day and OpenAI's day can
 * disagree across midnight.
 *
 * A search queued just before midnight and drained just after is marked here
 * against yesterday's dated key, while OpenAI stamps it on receipt — today. So
 * yesterday loses that conversion, and today can still take a fresh one,
 * showing two.
 *
 * Not hardened, on purpose. OpenAI stamping on receipt makes the boundary
 * fuzzy by however long the network takes, so closing this window narrows the
 * fuzz from ~10s to ~200ms rather than removing it, and no client-side rule
 * reconciles a local-day cap against a receipt-day stamp. The exposure is a
 * search inside the last seconds of a local day on a page whose SDK has not
 * loaded yet; the cost is one conversion on the wrong side of one midnight.
 */
const markWhenDelivered = (key: string): void => {
  if (sdkLoaded()) {
    markFired(key)
    return
  }

  awaitingConfirmation.add(key)
  const deadline = Date.now() + CONFIRM_DEADLINE_MS

  const confirm = (): void => {
    if (sdkLoaded()) {
      awaitingConfirmation.delete(key)
      markFired(key)
      return
    }
    if (Date.now() >= deadline) {
      // Deliberately unmarked. The next page load retries. The lease goes
      // now rather than at its TTL: this tab has stopped working on the
      // send, so holding other tabs off any longer only costs a conversion.
      awaitingConfirmation.delete(key)
      releaseLease(key)
      return
    }
    window.setTimeout(confirm, CONFIRM_POLL_MS)
  }

  window.setTimeout(confirm, CONFIRM_POLL_MS)
}

/**
 * Browsers without Web Locks, and a page that is being hidden, keep the
 * synchronous path: an asynchronous lock callback might never run there, and
 * losing the day's conversion outright is worse than the small duplicate risk.
 *
 * The callback must stay synchronous. The lock is released as soon as it
 * returns, which is the point — a callback that awaited the SDK would let a
 * frozen background tab (timers throttled to roughly one wake a minute) hold
 * the lock while a live tab is blocked from reporting at all.
 */
const withConversionLock = (key: string, attempt: () => void): void => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  const pageIsBeingHidden =
    typeof document !== 'undefined' && document.visibilityState === 'hidden'

  if (!locks || pageIsBeingHidden) {
    attempt()
    return
  }

  void locks
    .request(`${LOCK_PREFIX}${key}`, attempt)
    // A browser-level lock failure should not silently drop the day's only
    // conversion. The repeated guards keep this safe if the callback ran
    // first: the lease this tab already took makes the second pass a no-op.
    .catch(attempt)
}

/**
 * Guard, dispatch, lease — in that order, in one place, serialized across tabs
 * by a Web Lock as `tiktokConversions.ts` does. `localStorage` operations are
 * individually atomic, but an unlocked check→send sequence is not, so two tabs
 * finishing a search at the same moment could both send.
 *
 * ⚠️ Read that list again: lease, NOT mark. This is where the copy of TikTok
 * diverges, and the difference is forced. TikTok's send returns a synchronous
 * boolean, so it can mark inside the lock and be done. Pushing onto `oaiq.q`
 * returns nothing and cannot throw, so delivery is only observable later, by
 * polling — long after the lock has gone. The lease is what covers that gap;
 * the mark still waits for `markWhenDelivered`.
 *
 * The guards are repeated INSIDE the lock, after waiting, so the second tab
 * sees whatever the first tab left — a mark if it got that far, a lease if it
 * is still working.
 *
 * Meta does without this because a server-side copy of every Meta event
 * arrives independently. OpenAI's server copy does not help here: it shares
 * the id, so two browser sends plus the server one all lean on the same
 * OpenAI dedup this lock exists to avoid relying on.
 *
 * The id is deliberately deterministic rather than a random UUID. OpenAI's
 * documented rule is that repeats of the same pixel + event name + event id
 * collapse, first one received winning — which would also cover the cases this
 * lock cannot, such as a second device. We have read that rule but never
 * watched it work, so it is a backstop we do not rely on. The generated pixel
 * snippet omits the id entirely; sending one is also what keeps the api's
 * server-side copy from double-counting everything.
 */
const fireOnce = (
  event: OpenAiConversionEventType,
  shape: Record<string, string>,
  eventId: string,
  extraParams?: Record<string, unknown>,
  retryDetail?: string
): void => {
  if (typeof window === 'undefined') return

  const key = `${event}:${eventId}`

  const dispatchIfNeeded = (): void => {
    if (hasFired(key)) return
    if (!window.oaiq) return
    if (awaitingConfirmation.has(key)) return
    if (leaseHeld(key)) return

    // Before the send, not after: the whole point is that another tab reads it
    // during the window where there is no mark to read.
    takeLease(key)

    try {
      // Shape last: `type` is what tells OpenAI how to read the payload, so a
      // caller's extras must never be able to overwrite it.
      window.oaiq(
        'measure',
        event,
        { ...extraParams, ...shape },
        { event_id: eventId }
      )
    } catch {
      // Analytics must not break the app, and an unmarked failure stays
      // retryable on the next search — the record written before the lock is
      // still there, and this threw before `countPendingDispatch` could spend
      // an attempt on it.
      releaseLease(key)
      return
    }

    // The pixel has the event. Only now does this count against the cap.
    if (retryDetail !== undefined) countPendingDispatch(key)

    markWhenDelivered(key)
  }

  if (hasFired(key)) return

  // The server copy goes out here, ahead of the `window.oaiq` check a blocked
  // pixel fails, and with its own once-only record. Same id, so a pixel copy
  // that also lands is collapsed by OpenAI.
  relayOpenAiSearchConversion(event, eventId)

  // Before the lock, not inside it. `dispatchIfNeeded` runs as the
  // `navigator.locks` callback, and a callback is not guaranteed to run: a
  // page torn down between the request and the grant, or another context
  // holding the same lock while frozen (a frozen tab keeps its locks; only a
  // destroyed one releases them), leaves no record, no mark and no conversion.
  // `pageIsBeingHidden` only catches a page already hidden at call time.
  //
  // The cost is a record left behind when an inner guard legitimately declines
  // to send. That settles itself: the guard that declines is another tab
  // already sending THIS key, and its `markFired` drops this same record.
  if (retryDetail !== undefined) rememberPending(key, retryDetail)

  withConversionLock(key, dispatchIfNeeded)
}

/**
 * ChatGPT Ads conversion for an executed search, capped at one per browser per
 * day to match Meta and TikTok. Google deliberately stays per-search.
 *
 * The cap's identity, date rules and accepted limits live in
 * `dailyConversionId.ts`. Two layers enforce it, covering different failures:
 *   1. our own guard in `fireOnce`, which stops the redundant `oaiq` call, and
 *   2. OpenAI itself, collapsing repeats of one event name + event id — which
 *      covers the SAME browser losing its mark (cleared storage, private mode,
 *      a quota or policy failure in the try/catch). It does NOT cover a second
 *      device; nothing here can.
 *
 * `conversionId` should come from `dailyConversionId`. Callers that cannot
 * identify the user fall back to a per-search id — an uncapped conversion is
 * the conservative failure, since the alternative is losing it entirely.
 */
export const trackOpenAiDupePerformed = ({
  conversionId,
  searchType,
}: {
  conversionId: string
  searchType: DupeSearchType
}): void => {
  // ⚠️ The SDK validates every event against the field list its `type` selects
  // and DROPS the whole event — silently, with only a console warning — if it
  // carries anything undocumented. So this payload stays as narrow as the
  // event allows: nothing rides here that nobody reads.
  //
  // The search type USED to ride as `contents: [{ content_type: searchType }]`,
  // the one documented slot that could carry a label. QA removed it: OpenAI's
  // reporting column catalogue offers one count column per standard event and
  // no dimension derived from an event's own fields, so the label was never
  // going to surface as a breakdown. Decision 5's stated fallback applies and
  // the search-type breakdown lives in PostHog. Sending it anyway is not free
  // — an inert field is one more thing the field-list contract can reject, and
  // `content_type` means a media type to OpenAI, not whatever we put in it.
  fireOnce(
    SEARCH_EVENT,
    CONTENTS_SHAPE,
    conversionId,
    undefined,
    // Still carried, and still load-bearing even though it no longer reaches
    // the payload: `fireOnce` writes a pending record only when this argument
    // is defined, and the flush drops a search record whose detail is empty.
    // Pass nothing here and every search silently loses its cross-page retry.
    searchType
  )
}

const dropPending = (storageKey: string): void => {
  try {
    window.localStorage.removeItem(storageKey)
  } catch {
    // See hasFired.
  }
}

const readPendingRecords = (): PendingRecord[] => {
  const records: PendingRecord[] = []

  try {
    const total = window.localStorage.length

    for (let i = 0; i < total; i += 1) {
      const storageKey = window.localStorage.key(i)
      if (!storageKey || !storageKey.startsWith(PENDING_PREFIX)) continue

      records.push({
        storageKey,
        key: storageKey.slice(PENDING_PREFIX.length),
        ...parsePending(window.localStorage.getItem(storageKey) ?? ''),
      })
    }
  } catch {
    // See hasFired.
    return []
  }

  return records
}

/**
 * Whether a search conversion is still safe to report late.
 *
 * Strictly same-local-day, and deliberately stricter than the signup rule.
 * OpenAI stamps a conversion when it receives it, so flushing yesterday's
 * dupe this morning moves a conversion into the wrong day — which corrupts
 * the daily cost-per-dupe this whole module exists to make comparable. A
 * conversion reported on the wrong day is worse than one not reported.
 *
 * Dated keys carry their day (`…:<user>:<YYYY-MM-DD>`, from
 * `dailyConversionId`). The uncapped fallback keys do not, so those fall back
 * to when the record was written.
 */
const searchStillReportable = (record: PendingRecord): boolean => {
  if (DATED_KEY.test(record.key)) return record.key.endsWith(`:${todayStamp()}`)

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  return record.writtenAt >= startOfToday.getTime()
}

/**
 * Retry any conversion that was recorded but never confirmed delivered.
 *
 * Safe to call on every page load: a delivered conversion left no record, and
 * anything still here re-enters the ordinary dispatch, which re-checks the
 * durable mark, the lease and the in-page guards before sending.
 */
export const flushPendingOpenAiConversions = (): void => {
  if (typeof window === 'undefined') return

  const cutoff = Date.now() - PENDING_TTL_MS

  for (const record of readPendingRecords()) {
    const { key, writtenAt, attempts, detail } = record

    if (writtenAt < cutoff || attempts >= MAX_PENDING_ATTEMPTS) {
      dropPending(record.storageKey)
      continue
    }

    if (key.startsWith(`${REGISTRATION_EVENT}:`)) {
      const userId = key.slice(REGISTRATION_EVENT.length + 1)
      if (!userId) {
        dropPending(record.storageKey)
        continue
      }
      trackOpenAiAccountCreation(userId)
      continue
    }

    if (key.startsWith(`${SEARCH_EVENT}:`)) {
      const conversionId = key.slice(SEARCH_EVENT.length + 1)
      if (!conversionId || !detail) {
        dropPending(record.storageKey)
        continue
      }
      if (!searchStillReportable(record)) {
        dropPending(record.storageKey)
        continue
      }
      trackOpenAiDupePerformed({
        conversionId,
        searchType: detail as DupeSearchType,
      })
      continue
    }

    // An event this build does not recognise: a record left by an older or
    // newer bundle. Dropping it is the only safe move — we cannot rebuild a
    // payload for a shape we do not know, and the SDK discards an event that
    // carries anything unexpected.
    dropPending(record.storageKey)
  }
}

const pendingRegistrations = new Set<string>()

/**
 * Called only for non-anonymous users after maybe-register confirms
 * `existing === false`. The Firebase uid matches the other platforms' account
 * id; registration is never daily.
 *
 * Waits for the pixel queue stub, unlike a search: signup will not happen
 * again, so there is no next attempt to inherit the conversion. After 10s we
 * give up WITHOUT marking sent, so a later dispatch in the same session can
 * still succeed.
 *
 * The mark then waits again, on delivery rather than on dispatch — see
 * `markWhenDelivered`. A signup queued into an SDK that never loads stays
 * unmarked, which is what lets a later page load try again.
 *
 * ⚠️ This is the path where cross-tab duplication is the DEFAULT, not a race,
 * which is why it takes a lease. The retry runs from a mount effect, and at
 * mount the real SDK reliably has not arrived — the stub is
 * `afterInteractive`, the SDK is `lazyOnload`. Two tabs restored together
 * therefore both found no mark and both sent, every time, until the lease
 * existed.
 */
export const trackOpenAiAccountCreation = (userId: string): void => {
  if (typeof window === 'undefined') return
  const key = `${REGISTRATION_EVENT}:${userId}`
  // `awaitingConfirmation` matters here as much as in `fireOnce`: `dispatch`
  // clears `pendingRegistrations` as soon as it has queued the event, so a
  // second flush in the same page — a remount, or StrictMode's double effect
  // — would otherwise re-send one still waiting on the SDK.
  if (
    hasFired(key) ||
    pendingRegistrations.has(key) ||
    awaitingConfirmation.has(key)
  ) {
    return
  }
  pendingRegistrations.add(key)
  // The server copy — see `fireOnce`. Here too for the pending flush, so a
  // relay that failed at signup gets the same later-page-load retry.
  relayOpenAiRegistrationConversion(userId)
  // Up front, unlike a search: if the stub never appears at all there is no
  // send to hang this off, and that is precisely the case a signup cannot
  // recover from on its own.
  rememberPending(key, '')
  const deadline = Date.now() + 10_000

  const sendNow = (): void => {
    // Recheck inside the lock, so a tab that got here second sees whatever
    // the first left — a mark if it delivered, a lease if it is still going.
    if (hasFired(key) || leaseHeld(key)) {
      pendingRegistrations.delete(key)
      return
    }

    takeLease(key)

    try {
      window.oaiq?.(
        'measure',
        REGISTRATION_EVENT,
        { ...CUSTOMER_ACTION_SHAPE },
        { event_id: userId }
      )
    } catch {
      // A failed dispatch must neither interrupt signup nor mark it sent. The
      // lease goes back so the next tick — or another tab — can try, and the
      // retry record stays for a later page load if none of them get there.
      releaseLease(key)
      if (Date.now() < deadline) window.setTimeout(waitForStub, 200)
      else pendingRegistrations.delete(key)
      return
    }

    // The pixel has the event — see `countPendingDispatch`. Placed after the
    // call so the 200ms re-entry loop above cannot spend the whole cap inside
    // a single page load.
    countPendingDispatch(key)

    markWhenDelivered(key)
    pendingRegistrations.delete(key)
  }

  // Deliberately NOT wrapped in the Web Lock a search uses, and the lease is
  // doing the cross-tab work alone here.
  //
  // A lock would make this first dispatch asynchronous — the callback runs a
  // microtask later — and this path is the one that runs while a tab may be
  // going away. Losing a signup to a callback that never ran is a real cost;
  // the race the lock would close is two tabs reading "no lease" inside the
  // same tick of each other, having already passed the multi-second window
  // the lease closes. That residual falls to OpenAI's event-id dedup, which
  // is where cases we cannot see — a second device — land anyway.
  const waitForStub = (): void => {
    if (hasFired(key)) {
      pendingRegistrations.delete(key)
      return
    }

    // `typeof`, not truthiness: the global is declared non-optional, so TS
    // reads a bare check as always true. At runtime it is absent whenever the
    // pixel scripts are gated off or have not been injected yet.
    if (typeof window.oaiq === 'function') {
      sendNow()
      return
    }

    if (Date.now() >= deadline) {
      pendingRegistrations.delete(key)
      return
    }
    window.setTimeout(waitForStub, 200)
  }

  waitForStub()
}
